import React, {useContext, useEffect, useState, useCallback} from "react"
import {injectIntl} from "react-intl";
import Select, {SelectOption} from "@amzn/meridian/select";
import {LidarOverviewContext} from "./context/LidarOverviewContext";
import {ProvisioningContext} from "./context/ProvisioningContext";
import {
    getAPIGatewayPostRequest,
    getNodeId,
    renderStatusWithIcon
} from "../../util/Util";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import {getAPIGatewayDataFromParent} from "../../handler/ParentDataHandler";
import {
    BRANCH,
    LEAF,
    ENDPOINT_PUBLISH_COMMAND_TO_THIN_CLIENT,
    LIDAR_TESTER_ACTIVATION_INTERVAL_MS,
    PROVISION_REQUEST,
    SKYLARK_BRANCH_PROVISION_REQUEST,
    SKYLARK_LEAF_PROVISION_REQUEST,
    SKYLARK_BRANCH_PROVISION_TIMEOUT_IN_SECONDS,
    SKYLARK_BRANCH_PROVISION_DISPLAY_TIMEOUT_IN_SECONDS,
    SKYLARK_LEAF_PROVISION_TIMEOUT_IN_SECONDS,
    SKYLARK_PROVISIONING_ACTIONS,
    SUCCESS_STRING,
    IN_PROGRESS_STRING,
    ERROR_STRING,
    TIMEOUT_STRING,
    PRIMARY_TEXT_COLOUR,
    ONLINE_OK,
    TROUBLESHOOTING_URLS
} from "../../util/Constants";
import LidarTesterGridView from "./lidarTester/LidarTesterGridView";
import ProvisioningProgressIndicator from "./lidarTester/ProvisioningProgressIndicator";
import {
    getLidarTesterActivationRequest,
    BRANCH_STATUS_CONFIG,
    LEAF_STATUS_CONFIG,
    getConnectivityBadgeStyle,
    getStatusBadgeStyle
} from "../../util/LidarTesterUtil";
import {getLocalizedMessage} from "../../util/Localization";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";

const CONTAINER_STYLE = {
    overflow: 'auto'
};

const LidarTesterTool = ({intl}) => {

    const {viewModel, dispatch} = useContext(LidarOverviewContext);
    const {provisioningViewModel, provisioningDispatch} = useContext(ProvisioningContext);
    const [selectedMac, setSelectedMac] = useState(null);
    const [error, setError] = useState(false);
    const [showPageRefreshAlert, setShowPageRefreshAlert] = useState(false);
    const [showConnectivityAlert, setShowConnectivityAlert] = useState(false);
    const [connectivityAlertTimeout, setConnectivityAlertTimeout] = useState(null);
    const [branchProvisioningRequestTimeOut, setBranchProvisioningRequestTimeOut] = useState(null);
    const [leafProvisioningStatus, setLeafProvisioningStatus] = useState(null); // null, 'in-progress', 'success', 'error', 'timeout'
    const [leafProvisioningRequestTimeout, setProvisioningRequestLeafTimeout] = useState(null);
    const [leafProvisioningLocked, setLeafProvisioningLocked] = useState(false); // To prevent updates after timeout

    // Extract values from provisioningViewModel with default values
    const provisioningTriggered = provisioningViewModel ? provisioningViewModel.provisioningTriggered : false;
    const clusterStatus = provisioningViewModel ? provisioningViewModel.clusterStatus : null;
    const expectedBranchNodeCount = provisioningViewModel ? provisioningViewModel.expectedBranchNodeCount : 0;
    const detectedBranchNodeCount = provisioningViewModel ? provisioningViewModel.detectedBranchNodeCount : 0;
    const provisioningData = provisioningViewModel ? provisioningViewModel.provisioningData : [];

    const device = viewModel.lidarDeviceInformation[selectedMac];

    const activateLidarTester = useCallback(() => {
        const args = getAPIGatewayPostRequest(getLidarTesterActivationRequest(), ENDPOINT_PUBLISH_COMMAND_TO_THIN_CLIENT);
        getAPIGatewayDataFromParent(args).then(r => console.log("Lidar Tester Activated"));
    }, [])

    const onClickTriggerProvision = useCallback(() => {
        if (selectedMac && device) {
            // Check device connectivity before proceeding
            if (device.connectivity.toLowerCase() !== ONLINE_OK) {
                // Clear any existing timeout
                clearTimeout(connectivityAlertTimeout);
                
                setShowConnectivityAlert(true);
                
                // Set 5-second timeout to auto-hide alert
                const timeoutId = setTimeout(() => setShowConnectivityAlert(false), 5000);
                setConnectivityAlertTimeout(timeoutId);
                return;
            }

            // Reset leaf provisioning state for new attempt
            setLeafProvisioningStatus(null);
            setLeafProvisioningLocked(false);
            clearTimeout(leafProvisioningRequestTimeout);
            setProvisioningRequestLeafTimeout(null);

            // Initialize provisioning via the context
            provisioningDispatch({type: SKYLARK_PROVISIONING_ACTIONS.INIT_PROVISIONING});

            // Clear any existing timeout
            clearTimeout(branchProvisioningRequestTimeOut);
            // First stage: Branch provisioning
            const branchProvisionRequest = {
                nodeId: getNodeId(),
                commandType: PROVISION_REQUEST,
                commandParams: JSON.stringify({
                    event: SKYLARK_BRANCH_PROVISION_REQUEST,
                    lidarControllerIP: device.ip
                })
            };

            const args = getAPIGatewayPostRequest(branchProvisionRequest, ENDPOINT_PUBLISH_COMMAND_TO_THIN_CLIENT);
            getAPIGatewayDataFromParent(args)
                .then((response) => {
                    console.log(response);
                    if (response && response.status === 500) {
                        setShowPageRefreshAlert(true);
                        // Reset provisioning state on session expiration
                        provisioningDispatch({
                            type: SKYLARK_PROVISIONING_ACTIONS.RESET_PROVISIONING
                        });
                        return;
                    }
                    // Set a 25-second timeout for branch provisioning response
                    const newBranchProvisioningRequestTimeOut = setTimeout(() => {
                        provisioningDispatch({
                            type: SKYLARK_PROVISIONING_ACTIONS.SET_CLUSTER_STATUS,
                            data: {status: TIMEOUT_STRING}
                        });
                    }, SKYLARK_BRANCH_PROVISION_TIMEOUT_IN_SECONDS * 1000);
                    setBranchProvisioningRequestTimeOut(newBranchProvisioningRequestTimeOut);
                })
                .catch((error) => {
                    setError(true);
                });
        }
    }, [selectedMac, device, branchProvisioningRequestTimeOut, provisioningDispatch, provisioningViewModel, setError])

    // Combined useEffect for provisioning workflow management
    useEffect(() => {
        // Handle cluster status changes and trigger leaf provisioning
        if (clusterStatus && branchProvisioningRequestTimeOut) {
            clearTimeout(branchProvisioningRequestTimeOut);
            setBranchProvisioningRequestTimeOut(null);

            // If branch provisioning was successful, trigger leaf provisioning
            if (clusterStatus === SUCCESS_STRING && selectedMac && device && !leafProvisioningLocked) {
                setLeafProvisioningStatus(IN_PROGRESS_STRING);

                // Second stage: Leaf provisioning
                const leafProvisionRequest = {
                    nodeId: getNodeId(),
                    commandType: PROVISION_REQUEST,
                    commandParams: JSON.stringify({
                        event: SKYLARK_LEAF_PROVISION_REQUEST,
                        lidarControllerIP: device.ip
                    })
                };

                const args = getAPIGatewayPostRequest(leafProvisionRequest, ENDPOINT_PUBLISH_COMMAND_TO_THIN_CLIENT);
                getAPIGatewayDataFromParent(args)
                    .then(() => {
                        // Set a timeout for leaf provisioning response
                        const newLeafProvisioningRequestTimeout = setTimeout(() => {
                            // Only update if not already locked (prevents race conditions)
                            if (!leafProvisioningLocked) {
                                setLeafProvisioningStatus(TIMEOUT_STRING);
                                setLeafProvisioningLocked(true); // Lock status updates
                            }
                        }, SKYLARK_LEAF_PROVISION_TIMEOUT_IN_SECONDS * 1000);

                        setProvisioningRequestLeafTimeout(newLeafProvisioningRequestTimeout);
                    })
                    .catch((error) => {
                        if (!leafProvisioningLocked) {
                            setLeafProvisioningStatus(ERROR_STRING);
                            setLeafProvisioningLocked(true); // Lock status updates on error
                        }
                        setError(true);
                    });
            }
        }
    }, [clusterStatus, branchProvisioningRequestTimeOut, selectedMac, device, leafProvisioningLocked]);

    // Combined effect for initialization and cleanup
    useEffect(() => {
        // Load view data on mount
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});

        // Initial lidar tester activation
        activateLidarTester();

        // Set up interval for subsequent activations
        const lidarActivationInterval = setInterval(() => {
            activateLidarTester();
        }, LIDAR_TESTER_ACTIVATION_INTERVAL_MS);

        // Cleanup function
        return () => {
            clearInterval(lidarActivationInterval);
            // Clean up any timeouts when component unmounts
            clearTimeout(branchProvisioningRequestTimeOut);
            clearTimeout(leafProvisioningRequestTimeout);
        };
    }, [dispatch, activateLidarTester]); // Note: timeoutId and leafProvisioningRequestTimeout removed from deps to avoid re-running

    // Effect to monitor provisioning data and update leaf provisioning status
    useEffect(() => {
        // Only process if we're in leaf provisioning phase and not locked
        if (leafProvisioningStatus === IN_PROGRESS_STRING && !leafProvisioningLocked && provisioningData.length > 0) {
            // Check if we have leaf node data
            const leafNodes = provisioningData.filter(item => item.nodeType === LEAF);

            if (leafNodes.length > 0) {
                // Check if all leaf nodes are provisioned successfully
                const allLeafNodesProvisioned = leafNodes.length === expectedBranchNodeCount && 
                    leafNodes.every(node => node.expectedNodeCount === node.detectedNodeCount);

                if (allLeafNodesProvisioned) {
                    // All leaf nodes are provisioned successfully
                    setLeafProvisioningStatus(SUCCESS_STRING);
                    // Clear the timeout as we've received a successful response
                    clearTimeout(leafProvisioningRequestTimeout);
                    setProvisioningRequestLeafTimeout(null);
                } else {
                    // Some leaf nodes failed to provision
                    // Only update if we've received responses from all expected leaf nodes
                    const allLeafNodesResponded = leafNodes.length === (device && device.displayableAssetMappings ? device.displayableAssetMappings.length : 0);
                    if (allLeafNodesResponded) {
                        setLeafProvisioningStatus(ERROR_STRING);
                        // Clear the timeout as we've received responses from all nodes
                        clearTimeout(leafProvisioningRequestTimeout);
                        setProvisioningRequestLeafTimeout(null);
                    }
                }
            }
        }
    }, [provisioningData, leafProvisioningStatus, leafProvisioningLocked, device, leafProvisioningRequestTimeout]);

    // Reset provisioning state when device selection changes
    useEffect(() => {
        // Reset provisioning state via context
        provisioningDispatch({type: SKYLARK_PROVISIONING_ACTIONS.RESET_PROVISIONING});
        setError(false);
        setLeafProvisioningStatus(null);
        setLeafProvisioningLocked(false);

        // Clear any existing timeouts
        clearTimeout(branchProvisioningRequestTimeOut);
        setBranchProvisioningRequestTimeOut(null);
        clearTimeout(leafProvisioningRequestTimeout);
        setProvisioningRequestLeafTimeout(null);
        clearTimeout(connectivityAlertTimeout);
        setConnectivityAlertTimeout(null);
    }, [selectedMac, provisioningDispatch])  // Only depend on selectedMac and provisioningDispatch to prevent unnecessary re-renders. 
                                             // This useEffect block will be in action only when a different LidarController is being selected.

    // Helper functions for status display - eliminates nested ternary operators
    const getBranchStatusDisplay = useCallback((status, detectedCount, expectedCount) => {        
        const config = BRANCH_STATUS_CONFIG[status];

        return {
            backgroundColor: config.backgroundColor,
            textColor: config.textColor,
            message: config.getMessage(expectedCount, detectedCount)
        };
    }, []);

    const getLeafStatusDisplay = useCallback((status) => {
        const config = LEAF_STATUS_CONFIG[status];
        
        return {
            backgroundColor: config.backgroundColor,
            textColor: config.textColor,
            message: config.getMessage()
        };
    }, []);

    const renderStatusDisplay = useCallback((statusDisplay, status) => {
        if (!statusDisplay) return null;
        
        return (
            <div style={{
                width: '100%',
                padding: '8px',
                backgroundColor: statusDisplay.backgroundColor,
                color: statusDisplay.textColor,
                borderRadius: '4px',
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                {renderStatusWithIcon(status, statusDisplay.message, statusDisplay.textColor)}
            </div>
        );
    }, []);

    return (
        <div style={CONTAINER_STYLE}>
            <Row>
                <Text tag="label" type="b300" color={PRIMARY_TEXT_COLOUR}>
                    {getLocalizedMessage({intl, stringId: "lidarTester.select"})}
                </Text>
                <div style={{display: 'flex', alignItems: 'center', minHeight: '40px', overflowX: 'auto', gap: '10px'}}>
                    <Select
                        value={selectedMac}
                        onChange={setSelectedMac}
                        placeholder={getLocalizedMessage({intl, stringId: "lidarTester.select"})}
                        size="small"
                        width={250}>
                        {Object.values(viewModel.lidarDeviceInformation).map((device) => (
                            <SelectOption key={device.mac} value={device.mac}
                                label={device.location + " (" + device.mac + ")"} />
                        ))}
                    </Select>
                    {selectedMac && (
                        <Button
                            onClick={onClickTriggerProvision}
                            type="tertiary"
                            size="small"
                            style={{
                                backgroundColor: provisioningTriggered && clusterStatus === SUCCESS_STRING ? 'var(--button-background-color-tertiary-pressed)' :
                                               provisioningTriggered ? 'var(--button-background-color-tertiary-hover)' : undefined,
                                cursor: provisioningTriggered && !clusterStatus ? 'not-allowed' : 'pointer' // Change cursor when in progress
                            }}
                            disabled={provisioningTriggered && !clusterStatus} // Disable button during provisioning
                        >
                            <Text>{getLocalizedMessage({intl, stringId: "button.startProvision"})}</Text>
                        </Button>
                    )}
                </div>
            </Row>
            <div style={{marginTop: '10px'}}></div>
            {/* Device Status Row */}
            {selectedMac && device && (
                <Row style={{marginTop: '15px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '60px'}}>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <Text style={{fontWeight: 'bold'}}>
                                Connection Status:
                            </Text>
                            <div style={getConnectivityBadgeStyle(device.connectivity)}>
                                {device.connectivity && device.connectivity.toLowerCase() === ONLINE_OK ? 'Connected' : 'Disconnected'}
                            </div>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <Text style={{fontWeight: 'bold'}}>
                                Scanless Activation Status:
                            </Text>
                            <div style={getStatusBadgeStyle(device.status)}>
                                {device.status && device.status.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
                            </div>
                        </div>
                    </div>
                </Row>
            )}
            {/* Add spacing between control buttons and status messages */}
            <div style={{marginTop: '20px'}}></div>

            {/* Show provisioning in progress message when triggered but no status yet */}
            {provisioningTriggered && !clusterStatus && (
                <ProvisioningProgressIndicator nodeType={BRANCH} timeoutSeconds={SKYLARK_BRANCH_PROVISION_DISPLAY_TIMEOUT_IN_SECONDS} />
            )}

            {/* Show provisioning status when available */}
            {provisioningTriggered && clusterStatus && (
                <Row style={{marginBottom: '20px'}}>
                    {renderStatusDisplay(
                        getBranchStatusDisplay(clusterStatus, detectedBranchNodeCount, expectedBranchNodeCount),
                        clusterStatus
                    )}
                </Row>
            )}

            {/* Show leaf provisioning status */}
            {provisioningTriggered && clusterStatus === SUCCESS_STRING && leafProvisioningStatus && (
                <Row style={{marginBottom: '15px'}}>
                    <div style={{width: '100%'}}>
                        {renderStatusDisplay(
                            getLeafStatusDisplay(leafProvisioningStatus),
                            leafProvisioningStatus
                        )}
                    </div>
                </Row>
            )}
            {error && <Alert type="error">{getLocalizedMessage({intl, stringId: 'log.error'})}</Alert>}
            {showPageRefreshAlert && <Alert type="error">Session expired. Please refresh the page to continue.</Alert>}
            {showConnectivityAlert && (
                <Alert type="error" onClose={() => {
                    clearTimeout(connectivityAlertTimeout);
                    setShowConnectivityAlert(false);
                }}>
                    <span dangerouslySetInnerHTML={{
                        __html: `Cluster Controller is Disconnected. <a href="${TROUBLESHOOTING_URLS.SKYLARK_PROVISIONING}" target="_blank" rel="noopener noreferrer">Troubleshooting Guide</a>`
                    }} />
                </Alert>
            )}

            {/* Add spacing before grid view */}
            <div style={{marginTop: '10px'}}></div>
            {<LidarTesterGridView
                device={device}
                selectedMac={selectedMac}
                intl={intl}
                cartRecipesMap={viewModel.cartRecipesMap}
            />}
        </div>
    )
}

export default injectIntl(LidarTesterTool)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/LidarTesterTool.js